const shuffleArray = (items) => {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const normalizeBinyan = (value) => {
  const stringValue = String(value || '').trim();
  if (!stringValue) return '';
  const match = stringValue.match(/^\d+/);
  return match ? match[0] : stringValue;
};

const hasTooLongSecondaryRun = (sources, primaryBinyan, maxSecondaryRunLength) => {
  if (!maxSecondaryRunLength) return false;

  let currentRun = 0;
  for (const source of sources) {
    if (source !== primaryBinyan) {
      currentRun += 1;
      if (currentRun > maxSecondaryRunLength) {
        return true;
      }
    } else {
      currentRun = 0;
    }
  }

  return false;
};

const hasAdjacentDuplicateSecondary = (sources, primaryBinyan) => {
  for (let index = 0; index < sources.length - 1; index += 1) {
    const currentSource = sources[index];
    const nextSource = sources[index + 1];
    if (
      currentSource !== primaryBinyan &&
      nextSource !== primaryBinyan &&
      currentSource === nextSource
    ) {
      return true;
    }
  }

  return false;
};

const normalizeAllowedPairs = (allowedLastTwoPairs) => {
  if (!Array.isArray(allowedLastTwoPairs)) return null;

  return new Set(
    allowedLastTwoPairs
      .filter((pair) => Array.isArray(pair) && pair.length === 2)
      .map((pair) => `${normalizeBinyan(pair[0])}|${normalizeBinyan(pair[1])}`)
  );
};

export const getLowerBinyanNumbers = (primaryBinyan) => {
  const normalizedPrimary = Number(normalizeBinyan(primaryBinyan));
  if (!Number.isFinite(normalizedPrimary) || normalizedPrimary <= 1) {
    return [];
  }

  return Array.from({ length: normalizedPrimary - 1 }, (_, index) => String(index + 1));
};

export const buildAllowedTrailingBinyanPairs = (primaryBinyan, secondaryBinyans = []) => {
  const normalizedPrimary = normalizeBinyan(primaryBinyan);
  const normalizedSecondaries = [...new Set(
    secondaryBinyans
      .map((binyan) => normalizeBinyan(binyan))
      .filter((binyan) => binyan && binyan !== normalizedPrimary)
  )];
  const pairs = [[normalizedPrimary, normalizedPrimary]];

  normalizedSecondaries.forEach((secondaryBinyan) => {
    pairs.push([secondaryBinyan, normalizedPrimary]);
    pairs.push([normalizedPrimary, secondaryBinyan]);
  });

  normalizedSecondaries.forEach((firstSecondary) => {
    normalizedSecondaries.forEach((secondSecondary) => {
      if (firstSecondary !== secondSecondary) {
        pairs.push([firstSecondary, secondSecondary]);
      }
    });
  });

  return pairs;
};

export const buildFivePlusThreeBinyanRules = (
  primaryBinyan,
  secondaryBinyans = getLowerBinyanNumbers(primaryBinyan)
) => ({
  primaryBinyan: normalizeBinyan(primaryBinyan),
  firstPrimaryCount: 2,
  maxSecondaryRunLength: 2,
  forbidAdjacentDuplicateSecondary: true,
  allowedLastTwoPairs: buildAllowedTrailingBinyanPairs(primaryBinyan, secondaryBinyans),
  attempts: 1200
});

export const buildPrimaryHeavyBinyanRules = (primaryBinyan) => ({
  primaryBinyan: normalizeBinyan(primaryBinyan),
  firstPrimaryCount: 2,
  requireLastPrimary: true,
  attempts: 600
});

export const arrangeEntriesByBinyanRules = (entries, rules = {}) => {
  const primaryBinyan = normalizeBinyan(rules.primaryBinyan);
  const getSource = rules.getSource || ((entry) => entry.sourceBinyan);
  const firstPrimaryCount = Math.max(0, rules.firstPrimaryCount || 0);
  const allowedLastTwoPairs = normalizeAllowedPairs(rules.allowedLastTwoPairs);
  const attempts = Math.max(1, rules.attempts || 1);

  const buildCandidate = () => {
    const primaryEntries = shuffleArray(
      entries.filter((entry) => normalizeBinyan(getSource(entry)) === primaryBinyan)
    );
    const secondaryEntries = shuffleArray(
      entries.filter((entry) => normalizeBinyan(getSource(entry)) !== primaryBinyan)
    );
    const guaranteedOpening = primaryEntries.splice(0, Math.min(firstPrimaryCount, primaryEntries.length));
    return [...guaranteedOpening, ...shuffleArray([...primaryEntries, ...secondaryEntries])];
  };

  const isValidCandidate = (candidate) => {
    const sources = candidate.map((entry) => normalizeBinyan(getSource(entry)));

    if (sources.slice(0, firstPrimaryCount).some((source) => source !== primaryBinyan)) {
      return false;
    }

    if (rules.requireLastPrimary && sources[sources.length - 1] !== primaryBinyan) {
      return false;
    }

    if (hasTooLongSecondaryRun(sources, primaryBinyan, rules.maxSecondaryRunLength)) {
      return false;
    }

    if (rules.forbidAdjacentDuplicateSecondary && hasAdjacentDuplicateSecondary(sources, primaryBinyan)) {
      return false;
    }

    if (allowedLastTwoPairs && sources.length >= 2) {
      const trailingKey = `${sources[sources.length - 2]}|${sources[sources.length - 1]}`;
      if (!allowedLastTwoPairs.has(trailingKey)) {
        return false;
      }
    }

    return true;
  };

  let fallbackCandidate = buildCandidate();
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const candidate = buildCandidate();
    fallbackCandidate = candidate;
    if (isValidCandidate(candidate)) {
      return candidate;
    }
  }

  return fallbackCandidate;
};
